import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { faClockRotateLeft, faEye, faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { API_KEY_STATUS, API_KEY_STATUS_COLORS, formatApiKeyDate, formatApiKeyDateTime, getApiKeyStatus, resolveUserEmail } from "common/apiKeys";
import { ProhibitedArea } from "components";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;
const { Text } = Typography;

const DateCell = ({ value }) => (
    <Tooltip placement="topLeft" title={formatApiKeyDateTime(value)}>
        {formatApiKeyDate(value)}
    </Tooltip>
);

const ApiKeyList = ({
    apiKeyList,
    userList,
    onAddApiKey,
    onViewApiKey,
    onEditApiKey,
    onActivateDeactivateApiKey,
    onViewApiKeyHistory,
    onDeleteApiKey,
    permission,
}) => {
    if (!permission) {
        return <ProhibitedArea></ProhibitedArea>;
    }

    return (
        <FlexContainerLayout
            footer={
                <Button type="primary" disabled={!permission.canAdd} onClick={onAddApiKey} data-testid={CypressTestIds.API_KEY_LIST_ADD_BUTTON}>
                    <PlusOutlined /> Add API Key
                </Button>
            }
        >
            <Table
                size="small"
                rowKey="apiKeyId"
                dataSource={apiKeyList}
                pagination={false}
                className="flex size-full flex-1 flex-col [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-header-footer [&_thead]:text-header-footer"
                data-testid={CypressTestIds.API_KEY_LIST_TABLE}
            >
                <Column
                    width="6em"
                    render={(value, apiKey, index) => (
                        <div className="flex gap-2">
                            <Tooltip title="Edit API Key">
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    className={permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canEdit ? onEditApiKey(apiKey.apiKeyId) : null)}
                                    data-testid={CypressTestIds.API_KEY_LIST_EDIT_BUTTON}
                                />
                            </Tooltip>
                            <Tooltip title="View API Key">
                                <FontAwesomeIcon
                                    icon={faEye}
                                    className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canView ? onViewApiKey(apiKey.apiKeyId) : null)}
                                    data-testid={CypressTestIds.API_KEY_LIST_VIEW_BUTTON}
                                />
                            </Tooltip>
                            <Tooltip title="View API Key History">
                                <FontAwesomeIcon
                                    icon={faClockRotateLeft}
                                    className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canView ? onViewApiKeyHistory(apiKey.apiKeyId) : null)}
                                    data-testid={CypressTestIds.API_KEY_LIST_HISTORY_BUTTON}
                                />
                            </Tooltip>
                        </div>
                    )}
                />
                <Column
                    dataIndex="name"
                    title="Name"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, apiKey, index) => (
                        <Tooltip placement="topLeft" title={apiKey.description ? `${value} - ${apiKey.description}` : value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    width="7em"
                    title="Status"
                    render={(value, apiKey, index) => {
                        let status = getApiKeyStatus(apiKey);
                        return (
                            <Tag color={API_KEY_STATUS_COLORS[status]} data-testid={CypressTestIds.API_KEY_LIST_STATUS_TAG}>
                                {status}
                            </Tag>
                        );
                    }}
                />
                <Column
                    width="14%"
                    dataIndex="apiKeyId"
                    title="Key ID"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, apiKey, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            <Text className="font-mono">{value}</Text>
                        </Tooltip>
                    )}
                />
                <Column
                    width="12em"
                    dataIndex="maskedKey"
                    title="Secret Key"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, apiKey, index) => (
                        <Tooltip placement="topLeft" title="The full key was shown once when it was created.">
                            <Text className="font-mono">{value}</Text>
                        </Tooltip>
                    )}
                />
                <Column
                    width="9em"
                    dataIndex="businessAreaCount"
                    title="Business Areas"
                    render={(value, apiKey, index) => <>{value || 0}</>}
                />
                <Column width="6em" dataIndex="roleCount" title="Roles" render={(value, apiKey, index) => <>{value || 0}</>} />
                <Column width="8em" dataIndex="createdDate" title="Created" render={(value, apiKey, index) => <DateCell value={value} />} />
                <Column width="8em" dataIndex="expiresAt" title="Expires" render={(value, apiKey, index) => <DateCell value={value} />} />
                <Column
                    width="8em"
                    dataIndex="lastUsedDate"
                    title={
                        <span>
                            Last used{" "}
                            <Tooltip title="The last time this key successfully authenticated a request.">
                                <InfoCircleOutlined className="text-gray-400" />
                            </Tooltip>
                        </span>
                    }
                    render={(value, apiKey, index) => <DateCell value={value} />}
                />
                <Column
                    width="14%"
                    dataIndex="createdBy"
                    title="Created by"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, apiKey, index) => {
                        let email = resolveUserEmail(userList, value);
                        return (
                            <Tooltip placement="topLeft" title={email}>
                                {email}
                            </Tooltip>
                        );
                    }}
                />
                <Column
                    width="6em"
                    dataIndex="active"
                    title={"Active"}
                    render={(value, apiKey, index) => {
                        let expired = getApiKeyStatus(apiKey) === API_KEY_STATUS.EXPIRED;
                        return (
                            <Tooltip title={expired ? "This key has expired. Change its expiry date to use it again." : undefined}>
                                <Switch
                                    checked={value}
                                    disabled={!permission.canEdit || expired}
                                    onClick={(checked, event) => onActivateDeactivateApiKey(apiKey, checked)}
                                    data-testid={CypressTestIds.API_KEY_LIST_ACTIVE_SWITCH}
                                ></Switch>
                            </Tooltip>
                        );
                    }}
                />
                <Column
                    width="4em"
                    render={(value, apiKey, index) => {
                        return (
                            <Tooltip title="Delete API Key">
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    className={permission.canDelete ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canDelete ? onDeleteApiKey(apiKey) : null)}
                                    data-testid={CypressTestIds.API_KEY_LIST_DELETE_BUTTON}
                                />
                            </Tooltip>
                        );
                    }}
                />
            </Table>
        </FlexContainerLayout>
    );
};

export default ApiKeyList;
